import { useMemo, useState, useCallback } from "react";
import { SwatchCard } from "./SwatchCard";
import type { ColorRecord } from "../lib/color-engine";

export interface ColorCatalogProps {
  colors: ColorRecord[];
  familyNames: Record<string, string>; // familyId -> display name
  onSelect: (hex: string, shadeCode: string) => void;
  selectedShadeCode?: string;
}

/**
 * The 150-color catalog grid.
 *
 * Cross-device performance approach:
 *  - Grid columns scale with viewport via Tailwind breakpoints, but the
 *    CARD itself (SwatchCard) is identical work regardless of column
 *    count — we're not swapping in a heavier component on desktop.
 *  - No images anywhere in this view (see SwatchCard) — the catalog's
 *    performance is asset-free, so it doesn't degrade on slower mobile
 *    networks the way an image-grid would.
 *  - Family filtering narrows the rendered set instead of rendering all
 *    150 and hiding some with CSS — fewer DOM nodes on mobile, where DOM
 *    size affects scroll performance more than on desktop.
 *  - `content-visibility: auto` on each card (set in SwatchCard) means the
 *    browser doesn't do full layout/paint for off-screen cards, which is
 *    the same mechanism on every device class — no separate "mobile mode"
 *    with reduced content is needed.
 */
export function ColorCatalog({
  colors,
  familyNames,
  onSelect,
  selectedShadeCode,
}: ColorCatalogProps) {
  const [activeFamily, setActiveFamily] = useState<string | "all">("all");

  const families = useMemo(() => Object.entries(familyNames), [familyNames]);

  const visibleColors = useMemo(() => {
    if (activeFamily === "all") return colors;
    return colors.filter((c) => c.familyId === activeFamily);
  }, [colors, activeFamily]);

  const handleSelect = useCallback(
    (hex: string, shadeCode: string) => onSelect(hex, shadeCode),
    [onSelect]
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Family filter tabs — horizontally scrollable on narrow viewports
          rather than wrapping, so the control stays reachable with a thumb
          on mobile instead of pushing the grid down with multiple rows. */}
      <div
        className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]"
        role="tablist"
        aria-label="Filter by color family"
      >
        <FilterTab
          label="All"
          active={activeFamily === "all"}
          onClick={() => setActiveFamily("all")}
        />
        {families.map(([id, name]) => (
          <FilterTab
            key={id}
            label={name}
            active={activeFamily === id}
            onClick={() => setActiveFamily(id)}
          />
        ))}
      </div>

      {/* Grid: 2 cols on phones, scaling up to 6 on desktop. The card
          component itself does the same amount of work at every size —
          only the column count (a CSS concern) changes. */}
      <div
        className={[
          "grid gap-2.5",
          "grid-cols-2",
          "sm:grid-cols-3",
          "md:grid-cols-4",
          "lg:grid-cols-5",
          "xl:grid-cols-6",
        ].join(" ")}
      >
        {visibleColors.map((color) => (
          <SwatchCard
            key={color.id}
            shadeCode={color.shadeCode}
            name={color.name}
            hex={color.hex}
            isSelected={color.shadeCode === selectedShadeCode}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {visibleColors.length === 0 && (
        <p className="py-8 text-center text-sm text-[#6B6660]">
          No colors in this family yet.
        </p>
      )}
    </div>
  );
}

function FilterTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        // 44px min height applied to interactive controls uniformly —
        // this tab is exactly as easy to hit with a thumb as with a
        // mouse pointer, so there's no separate "mobile-friendly" variant.
        "min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-4 text-sm font-medium",
        "transition-colors duration-150",
        active
          ? "bg-[#52606D] text-white"
          : "bg-[#F7F6F3] text-[#232220] hover:bg-[#EDEBE6] border border-[#D9D5CC]",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "focus-visible:outline-[#52606D]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
